import { http, onCleanup, onMount, reactive } from 'cdui-js';
import { userState } from '../../../stores/user';
import { errorMessage } from './shared';
import { VerifyCaptcha, isBankSliderCaptcha, type BankSliderCaptcha, type CaptchaPhase } from './VerifyCaptcha';
import { bankVerifyText, maskBankEmail, maskBankPhone } from './verifyCopy';

export type CodeConfirm = {
  verifyCode: string;
  validType: 0 | 1;
};

type Channel = 'phone' | 'email';

type CodeSheetProps = {
  error: string;
  submitting: boolean;
  onClose: () => void;
  onEdit: () => void;
  onConfirm: (payload: CodeConfirm) => void;
};

const HOLD_MS = 600;

const textOf = (value: unknown) => (value == null ? '' : String(value).trim());

const silent = () => ({ headers: { 'X-No-Toast': '1' } });

const sendHeaders = () => ({
  headers: {
    'X-No-Toast': '1',
    userId: String(userState.id || ''),
  },
});

const readKeyboard = () => {
  const viewport = window.visualViewport;
  if (!viewport) return 0;
  const raw = Math.max(0, Math.round(window.innerHeight - viewport.height));
  if (raw < 80) return 0;
  return Math.min(raw, Math.round(window.innerHeight * 0.7));
};

export const CodeSheet = (props: CodeSheetProps) => {
  const state = reactive({
    channel: 'phone' as Channel,
    code: '',
    prefix: '',
    countdown: 0,
    sending: false,
    localError: '',
    keyboard: 0,
    dragY: 0,
    dragging: false,
    puzzleOpen: false,
    puzzle: null as BankSliderCaptcha | null,
    puzzlePhase: 'idle' as CaptchaPhase,
    puzzleStatus: '',
    puzzleBroken: '',
  });

  let generation = 0;
  let timer: ReturnType<typeof setInterval> | null = null;
  let holdTimer: ReturnType<typeof setTimeout> | null = null;
  let swipeStartY = 0;
  let swipeActive = false;

  const text = () => bankVerifyText();

  const stopCountdown = () => {
    if (timer) clearInterval(timer);
    timer = null;
  };

  const clearHold = () => {
    if (holdTimer) clearTimeout(holdTimer);
    holdTimer = null;
  };

  const startCountdown = () => {
    stopCountdown();
    state.countdown = 120;
    timer = setInterval(() => {
      if (state.countdown <= 1) {
        stopCountdown();
        state.countdown = 0;
        return;
      }
      state.countdown -= 1;
    }, 1000);
  };

  const showLocal = (message: string) => {
    props.onEdit();
    state.localError = message;
  };

  const closePuzzle = () => {
    generation += 1;
    clearHold();
    state.puzzleOpen = false;
    state.puzzle = null;
    state.puzzlePhase = 'idle';
    state.puzzleStatus = '';
    state.puzzleBroken = '';
    state.sending = false;
  };

  const dismiss = () => {
    if (props.submitting) return;
    props.onClose();
  };

  onCleanup(() => {
    generation += 1;
    stopCountdown();
    clearHold();
  });

  onMount(() => {
    const syncKeyboard = () => {
      state.keyboard = readKeyboard();
    };
    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', syncKeyboard);
    viewport?.addEventListener('scroll', syncKeyboard);
    window.addEventListener('resize', syncKeyboard);
    onCleanup(() => {
      viewport?.removeEventListener('resize', syncKeyboard);
      viewport?.removeEventListener('scroll', syncKeyboard);
      window.removeEventListener('resize', syncKeyboard);
    });
  });

  const contact = () => {
    const copy = text();
    if (state.channel === 'email') {
      const email = textOf(userState.email);
      return email ? maskBankEmail(email) : copy.noEmail;
    }
    const phone = textOf(userState.phone);
    return phone ? maskBankPhone(phone) : copy.noPhone;
  };

  const targetAccount = () => (state.channel === 'email' ? textOf(userState.email) : textOf(userState.phone));

  const countryCode = () => (state.channel === 'phone' ? textOf(userState.countryCode) : '');

  const switchChannel = () => {
    if (props.submitting || state.puzzleOpen) return;
    generation += 1;
    clearHold();
    stopCountdown();
    state.sending = false;
    state.countdown = 0;
    state.channel = state.channel === 'phone' ? 'email' : 'phone';
    state.code = '';
    state.prefix = '';
    state.localError = '';
    props.onEdit();
  };

  const refreshPuzzle = async () => {
    if (state.puzzlePhase === 'checking' || state.puzzlePhase === 'ok' || state.puzzlePhase === 'refreshing') return;
    const gen = generation;
    state.puzzlePhase = 'refreshing';
    state.puzzleStatus = '';
    state.puzzleBroken = '';
    try {
      const captcha = await http.post('/user/login/sliderSend', {}, silent());
      if (gen !== generation) return;
      if (!isBankSliderCaptcha(captcha)) {
        state.puzzlePhase = 'idle';
        state.puzzleBroken = text().unavailable;
        return;
      }
      state.puzzle = captcha;
      state.puzzlePhase = 'idle';
    } catch (error) {
      if (gen !== generation) return;
      state.puzzlePhase = 'idle';
      state.puzzleBroken = errorMessage(error, text().loadFail);
    }
  };

  const failPuzzle = (gen: number, message: string) => {
    state.puzzlePhase = 'bad';
    state.puzzleStatus = message;
    clearHold();
    holdTimer = setTimeout(() => {
      if (gen !== generation) return;
      void refreshPuzzle();
    }, HOLD_MS);
  };

  const finishSend = async (gen: number, channel: Channel, captcha?: { validateCode: string; slideX: number }) => {
    const contact = channel === 'email' ? textOf(userState.email) : textOf(userState.phone);
    const body: Record<string, unknown> = {
      sendCodeType: channel === 'email' ? '1' : '0',
      account: textOf(userState.userName),
      contact,
      useType: 114,
    };
    const code = channel === 'phone' ? textOf(userState.countryCode) : '';
    if (code) body.countryCode = code;
    if (captcha) {
      body.validateCode = captcha.validateCode;
      body.slideX = captcha.slideX;
    }
    const res = await http.post('/user/code/verifySend', body, sendHeaders());
    if (gen !== generation) return 'stale' as const;
    // code 200 才走到这里。data 是前缀字符串；null 表示没有前缀，确认时只提交数字。
    if (res != null && typeof res !== 'string') return 'bad' as const;
    state.prefix = typeof res === 'string' ? res.trim() : '';
    startCountdown();
    return 'ok' as const;
  };

  const onPuzzleSolved = async (slideX: number) => {
    if (state.puzzlePhase !== 'idle' || !state.puzzle) return;
    const gen = generation;
    const channel = state.channel;
    const validateCode = String(state.puzzle.validateCode || '').trim();
    state.puzzlePhase = 'checking';
    state.puzzleStatus = text().verifying;
    state.puzzleBroken = '';
    try {
      const result = await finishSend(gen, channel, { validateCode, slideX });
      if (gen !== generation || result === 'stale') return;
      if (result !== 'ok') {
        failPuzzle(gen, text().verifyFail);
        return;
      }
      state.puzzlePhase = 'ok';
      state.puzzleStatus = text().passed;
      clearHold();
      holdTimer = setTimeout(() => {
        if (gen !== generation) return;
        state.puzzleOpen = false;
        state.puzzle = null;
        state.puzzlePhase = 'idle';
        state.puzzleStatus = '';
        state.sending = false;
      }, HOLD_MS);
    } catch (error) {
      if (gen !== generation) return;
      failPuzzle(gen, errorMessage(error, text().verifyFail));
    }
  };

  const send = async () => {
    if (state.sending || state.countdown > 0 || props.submitting) return;
    const copy = text();
    const account = targetAccount();
    if (!account) {
      showLocal(copy.sendFail);
      return;
    }
    const gen = ++generation;
    const channel = state.channel;
    state.sending = true;
    state.localError = '';
    props.onEdit();
    try {
      const params = new URLSearchParams();
      params.set('sendCodeType', channel === 'email' ? '1' : '0');
      params.set('account', account);
      const dial = countryCode();
      if (dial) params.set('countryCode', dial);
      const required = await http.get(`/user/login/sliderRequired?${params.toString()}`, silent());
      if (gen !== generation) return;
      if (typeof required !== 'boolean') {
        showLocal(copy.unavailable);
        state.sending = false;
        return;
      }
      if (!required) {
        const result = await finishSend(gen, channel);
        if (gen !== generation || result === 'stale') return;
        if (result !== 'ok') showLocal(copy.sendFail);
        state.sending = false;
        return;
      }
      const captcha = await http.post('/user/login/sliderSend', {}, silent());
      if (gen !== generation) return;
      if (!isBankSliderCaptcha(captcha)) {
        showLocal(copy.unavailable);
        state.sending = false;
        return;
      }
      state.puzzle = captcha;
      state.puzzlePhase = 'idle';
      state.puzzleStatus = '';
      state.puzzleBroken = '';
      state.puzzleOpen = true;
    } catch (error) {
      if (gen !== generation) return;
      showLocal(errorMessage(error, copy.sendFail));
      state.sending = false;
    }
  };

  const confirm = () => {
    if (props.submitting) return;
    const code = state.code.trim();
    if (!code) {
      showLocal(text().codeRequired);
      return;
    }
    const verifyCode = state.prefix ? `${state.prefix}-${code}` : code;
    props.onConfirm({
      verifyCode,
      validType: state.channel === 'email' ? 1 : 0,
    });
  };

  const message = () => props.error || state.localError;

  const onTouchStart = (event: TouchEvent) => {
    if (props.submitting || state.puzzleOpen) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('button, input, textarea')) return;
    if (event.touches.length !== 1) return;
    swipeStartY = event.touches[0].clientY;
    swipeActive = true;
    state.dragging = true;
  };

  const onTouchMove = (event: TouchEvent) => {
    if (!swipeActive || event.touches.length !== 1) return;
    const delta = event.touches[0].clientY - swipeStartY;
    state.dragY = delta > 0 ? delta : 0;
  };

  const onTouchEnd = () => {
    if (!swipeActive) return;
    swipeActive = false;
    state.dragging = false;
    if (state.dragY > 72 && !props.submitting) {
      state.dragY = 0;
      dismiss();
      return;
    }
    state.dragY = 0;
  };

  const sheetStyle = () => {
    const style: Record<string, string> = {
      transform: state.dragY > 0 ? `translateY(${state.dragY}px)` : 'none',
      transition: state.dragging ? 'none' : 'transform 0.2s ease',
    };
    style.bottom = state.keyboard > 0 ? `${state.keyboard}px` : '0px';
    style['padding-bottom'] = state.keyboard > 0 ? '0px' : '50px';
    return style;
  };

  const sendClass = () => {
    if (state.sending) return 'bank-verify-send is-busy';
    if (state.countdown > 0) return 'bank-verify-send is-countdown';
    return 'bank-verify-send';
  };

  return (
    <div class="bank-verify-mask" onclick={() => dismiss()}>
      <div
        class="bank-verify-sheet"
        style={sheetStyle()}
        onclick={(event) => event.stopPropagation?.()}
        ontouchstart={onTouchStart}
        ontouchmove={onTouchMove}
        ontouchend={onTouchEnd}
        ontouchcancel={onTouchEnd}
      >
        <div class="bank-verify-grabber" />
        <div class="bank-verify-title">{text().title}</div>
        <p class="bank-verify-hint">{text().sentTo}</p>
        <p class="bank-verify-contact">{contact()}</p>
        <div class={`bank-verify-field${message() ? ' is-error' : ''}`}>
          <input
            inputmode="numeric"
            pattern="[0-9]*"
            autocomplete="one-time-code"
            placeholder={text().placeholder}
            value={state.code}
            oninput={(event) => {
              const input = event.target as HTMLInputElement;
              const next = input.value.replace(/\D/g, '');
              input.value = next;
              if (next === state.code) return;
              state.code = next;
              state.localError = '';
              props.onEdit();
            }}
          />
          <button
            type="button"
            class={sendClass()}
            disabled={state.sending || state.countdown > 0}
            onclick={() => {
              void send();
            }}
          >
            {state.sending ? <span class="bank-verify-spin" /> : state.countdown > 0 ? `${state.countdown}s` : text().send}
          </button>
        </div>
        {message() ? <p class="bank-verify-err">{message()}</p> : null}
        <div class="bank-verify-switch">
          <span class="bank-verify-switch-label">{text().switchTo}</span>
          <button type="button" class="bank-verify-switch-target" onclick={() => switchChannel()}>
            {state.channel === 'phone' ? text().email : text().sms}
          </button>
        </div>
        <button type="button" class="bank-verify-confirm" disabled={props.submitting} onclick={() => confirm()}>
          {props.submitting ? <span class="bank-verify-spin" /> : text().confirm}
        </button>
      </div>
      {state.puzzleOpen && state.puzzle ? (
        <VerifyCaptcha
          captcha={state.puzzle}
          phase={state.puzzlePhase}
          statusText={state.puzzleStatus}
          brokenText={state.puzzleBroken}
          onClose={() => closePuzzle()}
          onRefresh={() => {
            void refreshPuzzle();
          }}
          onSolved={(slideX) => {
            void onPuzzleSolved(slideX);
          }}
        />
      ) : null}
    </div>
  );
};
